import React, { useContext, useState } from 'react';
import {
  Grid,
  Typography,
  Avatar,
  Divider,
  List,
  useTheme,
  Paper,
  Box,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InventoryIcon from '@mui/icons-material/Inventory';
import InfoIcon from '@mui/icons-material/Info';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import {
  IPickupTask,
  IDeliveryBatch,
  IOrderDeliveryTask,
} from '../../../interfaces/delivery-company/tasks';
import { TimelineCard } from '../../delivery-company-components/order/order-timeline';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ErrorIcon from '@mui/icons-material/Error';
import NotesIcon from '@mui/icons-material/Notes';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PickupTaskDialog from '../../delivery-company-components/pickup-tasks/pickup-task-dialog';
import DeliveryBatchDialog from '../../delivery-company-components/delivery-tasks/delivery-task-dialog';
import { showPickupTaskState } from '../styles/pickup-task.styles';
import { showDeliveryBatchState } from '../styles/delivery-batch.styles';
import { OrderDeliveryTaskState } from '../../../enums/tasks';
import { getSortedTasks, OrderedTask } from './task_history_sorter';
import { showDeliveryTaskState } from '../styles/delivery-task.styles';
import { displayMoney, Money } from '../../../utils/money';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { UserType } from '../../../enums/users';
import MerchantPickupTaskDialog from '../../merchant-components/merchant-tasks/merchant-tasks-listing/pickup-task-dialog';
import {
  Check,
  Moped,
  Pending,
  Preview,
  WarehouseOutlined,
} from '@mui/icons-material';
import DeliveryTaskCommission from '../delivery-task-commission/delivery-task-commission';
import {
  DriverInvoiceState,
  IDriverInvoice,
} from '../../../interfaces/common/driver-invoices';
import {
  IMerchantOrdersInvoice,
  MerchantOrdersInvoiceState,
} from '../../../interfaces/common/merchant-invoices';
import { API_ENDPOINT } from '../../../configurations/global.config';
import axios from 'axios';
import { useSnackBar } from '../../../contexts/snackbar';
import InvoicePDFDialog from '../invoice-pdf-dialog/invoice-pdf-dialog';
import { OrderType } from '../../../interfaces/delivery-company/orders';
import { Hourglass } from 'lucide-react';
import { DeliveryState } from '../../../enums/orders';
import { emptyDriver } from '../../delivery-company-components/drivers/driver-forms/driver-form.utils';
import { user_is_admin } from '../../../utils/permissions';
import { useInvoicePDFViewer } from '../../../hooks/use-invoice-pdf-viewer';
import { PDFViewerDialog } from '../pdf-viewer-dialog/pdf-viewer-dialog';
import ForceDeliveryActionDialog from '../force-delivery-action-dialog/force-delivery-action-dialog';
import { useSelectedBranch } from '../../../contexts/selected-branch-context';
import { canEditItem } from '../../../utils/branch-permissions';

interface TaskHistoryCardProps {
  pickupTasks?: IPickupTask[];
  deliveryTasks?: IOrderDeliveryTask[];
  color: string;
  updateDeliveryTask: (t: IOrderDeliveryTask) => void;
  order: OrderType;
}

export const TaskHistoryCard: React.FC<TaskHistoryCardProps> = ({
  pickupTasks,
  deliveryTasks,
  color,
  updateDeliveryTask,
  order,
}) => {
  const theme = useTheme();
  const [openDeliveryBatch, setOpenDeliveryBatch] =
    useState<IDeliveryBatch | null>(null);
  const [openPickupTask, setOpenPickupTask] = useState<IPickupTask | null>(
    null
  );
  const { user, config } = useContext(AuthContext);
  const { selectedBranch } = useSelectedBranch();
  
  // Check if the order can be edited based on branch
  const canEditOrderBasedOnBranch = canEditItem(order.branch, selectedBranch);

  const [invoiceData, setInvoiceData] = React.useState<
    IDriverInvoice | IMerchantOrdersInvoice | null
  >(null);

  const [lockedInvoice, setLockedInvoice] = React.useState<
    IDriverInvoice | IMerchantOrdersInvoice | null
  >(null);

  const [invoiceLockOperation, setInvoiceLockOperation] = useState<
    'view' | 'delete' | null
  >(null);

  const { showMessage } = useSnackBar();

  // State for force action dialog
  const [forceActionDialogOpen, setForceActionDialogOpen] = useState(false);
  const [forceActionTask, setForceActionTask] =
    useState<IOrderDeliveryTask | null>(null);
  const [forceActionType, setForceActionType] = useState<'complete' | 'fail'>(
    'complete'
  );

  //use effect to console the forceActionTask
  React.useEffect(() => {
    console.log('Force Action Task:', forceActionTask);
  }, [forceActionTask]);

  // Use the invoice PDF viewer hook
  const {
    pdfUrl,
    isLoading: isPdfLoading,
    showPdfViewer,
    pdfTitle,
    pdfFilename,
    fetchAndViewInvoice,
    closePdfViewer,
  } = useInvoicePDFViewer();

  const fetchInvoiceContent = async (
    invoice: IDriverInvoice | IMerchantOrdersInvoice | null,
    target: 'driver' | 'merchant'
  ) => {
    if (!invoice) return;
    setLockedInvoice(invoice);
    setInvoiceLockOperation('view');

    try {
      if (target === 'driver') {
        // Use the hook for driver invoices (opens in PDF viewer)
        await fetchAndViewInvoice(invoice as IDriverInvoice, 'driver');
      } else {
        // Legacy approach for merchant invoices (opens in custom dialog)
        const endpoint = `${API_ENDPOINT}/api/v1/merchant-invoice/${invoice.id}/`;
        const response = await axios.get(endpoint, config);
        const data: IMerchantOrdersInvoice = response.data;
        setInvoiceData(data);
      }
    } catch (e) {
      showMessage('Failed to load invoice content', 'error');
    } finally {
      setLockedInvoice(null);
      setInvoiceLockOperation(null);
    }
  };

  const isMerchant = user?.type === UserType.MERCHANT;

  // Helper function to get purpose label and color (matching driver_invoices_listing.tsx)
  const getPurposeDetails = (purpose: 'NET' | 'COLLECTION' | 'COMMISSION') => {
    const purposeLabels = {
      NET: 'Net Settlement',
      COLLECTION: 'Collection',
      COMMISSION: 'Commission',
    };
    const purposeColors = {
      NET: 'primary' as const,
      COLLECTION: 'success' as const,
      COMMISSION: 'info' as const,
    };
    return {
      label: purposeLabels[purpose] || purpose,
      color: purposeColors[purpose] || ('default' as const),
    };
  };

  // Helper function to render invoice chip with clean, professional design
  const renderInvoiceChip = (
    invoice: IDriverInvoice,
    type: 'driver' | 'commission',
    isLocked: boolean
  ) => {
    const purposeDetails = getPurposeDetails(invoice.purpose);
    const isPaid = invoice.status === DriverInvoiceState.PAID;

    return (
      <Box
        key={invoice.id}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          p: 0.75,
          pr: 1.5,
          border: '1px solid',
          borderColor: isPaid ? 'success.main' : 'warning.main',
          borderRadius: 2,
          backgroundColor: isPaid ? 'success.lighter' : 'warning.lighter',
          cursor: isLocked ? 'default' : 'pointer',
          opacity: isLocked ? 0.6 : 1,
          transition: 'all 0.2s',
          '&:hover': isLocked
            ? {}
            : {
                boxShadow: 2,
                transform: 'translateY(-1px)',
              },
        }}
        onClick={() => !isLocked && fetchInvoiceContent(invoice, 'driver')}
      >
        {isLocked ? (
          <CircularProgress size={16} sx={{ mr: 0.5 }} />
        ) : isPaid ? (
          <Check sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
        ) : (
          <Pending sx={{ fontSize: 16, color: 'warning.main', mr: 0.5 }} />
        )}

        <Typography variant="body2" sx={{ fontWeight: 500, mr: 1 }}>
          Invoice #{invoice.id}
        </Typography>

        <Chip
          label={purposeDetails.label}
          size="small"
          color={purposeDetails.color}
          variant="outlined"
          sx={{
            height: 20,
            fontSize: '0.7rem',
            fontWeight: 500,
            '& .MuiChip-label': { px: 1 },
          }}
        />
      </Box>
    );
  };

  const handleOpenDeliveryBatch = (task: IDeliveryBatch) => {
    setOpenDeliveryBatch(task);
  };

  const handleCloseDeliveryBatch = () => {
    setOpenDeliveryBatch(null);
  };

  const handleOpenPickupTask = (task: IPickupTask) => {
    setOpenPickupTask(task);
  };

  const handleClosePickupTask = () => {
    setOpenPickupTask(null);
  };

  const handleForceAction = (
    task: IOrderDeliveryTask,
    actionType: 'complete' | 'fail'
  ) => {
    setForceActionTask(task);
    setForceActionType(actionType);
    setForceActionDialogOpen(true);
  };

  const handleForceActionSuccess = () => {
    setForceActionDialogOpen(false);
    setForceActionTask(null);
    //TODO: Reload the order page to refresh the task data
  };

  const canForceAction = (task: IOrderDeliveryTask) => {
    const isAdminOrOrg = user_is_admin(user);
    const isTaskActionable =
      task.status === OrderDeliveryTaskState.OUT_FOR_DELIVERY ||
      task.status === OrderDeliveryTaskState.IN_PROGRESS ||
      task.status === OrderDeliveryTaskState.AWAITING_RETURN;
    return !isMerchant && isAdminOrOrg && isTaskActionable;
  };

  const renderPickupTaskItem = (task: IPickupTask) => {
    const date = task.creation_date
      ? new Date(task.creation_date).toLocaleString()
      : 'No date';
    const driverName = 'driver' in task ? task.driver?.name : null;
    const driverPhone = 'driver' in task ? task.driver?.phone_number : null;
    const notes = 'notes' in task ? task.notes : [];
    const ordersCount = task.package_count;
    const failureReason = 'failure_reason' in task ? task.failure_reason : null;
    const commission =
      'driver_commission' in task ? task.driver_commission : [];

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <Avatar
          sx={{
            bgcolor: theme.palette.info.light,
            color: theme.palette.getContrastText(theme.palette.info.light),
          }}
        >
          {<InventoryIcon />}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Pickup Task #{task.id}
              </Typography>
              {showPickupTaskState(task.status)}
            </Box>
            <Typography variant="caption" color="text.secondary">
              <CalendarTodayIcon
                sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }}
              />
              {date}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <Chip
              icon={<AssignmentIcon fontSize="small" />}
              label={`${ordersCount} ${ordersCount === 1 ? 'package' : 'packages'}`}
              size="small"
              variant="outlined"
            />

            {driverName && (
              <Chip
                icon={<Moped fontSize="small" />}
                label={`${driverName} ${driverPhone ? `(${driverPhone})` : ''}`}
                size="small"
                variant="outlined"
              />
            )}

            {!isMerchant && commission.length > 0 && user_is_admin(user) && (
              <Chip
                icon={<AttachMoneyIcon fontSize="small" />}
                label={`Commission: ${commission.map((c) => `${c[1]} ${c[0]}`).join(', ')}`}
                size="small"
                variant="outlined"
              />
            )}
          </Box>

          {failureReason && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                backgroundColor: 'error',
                p: 1,
                borderRadius: 1,
                mb: 1,
              }}
            >
              <ErrorIcon color="error" fontSize="small" />
              <Typography variant="body2" color="error.main">
                {failureReason}
              </Typography>
            </Box>
          )}

          {notes && notes.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                backgroundColor: theme.palette.grey[100],
                p: 1,
                borderRadius: 1,
              }}
            >
              <NotesIcon color="info" fontSize="small" />
              <Box>
                {notes.map((note, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    color="text.secondary"
                  >
                    • {note}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          <Box display="flex" gap={1} flexWrap="wrap" mt={1} mb={1}>
            {!isMerchant &&
              task.invoice &&
              renderInvoiceChip(
                task.invoice,
                'driver',
                lockedInvoice?.id === task.invoice.id &&
                  invoiceLockOperation === 'view'
              )}

            {!isMerchant &&
              task.commission_invoice &&
              renderInvoiceChip(
                task.commission_invoice,
                'commission',
                lockedInvoice?.id === task.commission_invoice.id &&
                  invoiceLockOperation === 'view'
              )}
          </Box>

          <Button
            size="small"
            variant="outlined"
            sx={{ mt: 1 }}
            onClick={() => handleOpenPickupTask(task)}
            startIcon={<VisibilityIcon />}
          >
            View Full Details
          </Button>
        </Box>
      </Box>
    );
  };

  const renderDeliveryBatch = (task: IDeliveryBatch) => {
    const date = task.creation_date
      ? new Date(task.creation_date).toLocaleString()
      : 'No date';
    const driverName = 'driver' in task ? task.driver?.name : null;
    const driverPhone = 'driver' in task ? task.driver?.phone_number : null;
    const status = task.status;
    const ordersCount = task.package_count;

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <Avatar
          sx={{
            bgcolor: theme.palette.primary.light,
            color: theme.palette.getContrastText(theme.palette.primary.light),
          }}
        >
          {<LocalShippingIcon />}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Batch #{task.id}
              </Typography>
              {showDeliveryBatchState(status)}
            </Box>
            <Typography variant="caption" color="text.secondary">
              <CalendarTodayIcon
                sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }}
              />
              {date}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            {!isMerchant && (
              <Chip
                icon={<AssignmentIcon fontSize="small" />}
                label={`${ordersCount} ${ordersCount === 1 ? 'package' : 'packages'}`}
                size="small"
                variant="outlined"
              />
            )}

            {driverName && (
              <Chip
                icon={<Moped fontSize="small" />}
                label={`${driverName} ${driverPhone ? `(${driverPhone})` : ''}`}
                size="small"
                variant="outlined"
              />
            )}
          </Box>

          {!isMerchant && (
            <Button
              size="small"
              variant="outlined"
              sx={{ mt: 1 }}
              onClick={() => handleOpenDeliveryBatch(task)}
              startIcon={<VisibilityIcon />}
            >
              View Full Details
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  const renderDeliveryTask = (task: IOrderDeliveryTask) => {
    const date = task.creation_date
      ? new Date(task.creation_date).toLocaleString()
      : 'No date';
    const driver = task.driver;
    const notes: string[] = task.notes ?? [];

    const failureReason = 'failure_reason' in task ? task.failure_reason : null;

    const status = task.status;
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <Avatar
          sx={{
            bgcolor: theme.palette.primary.light,
            color: theme.palette.getContrastText(theme.palette.primary.light),
          }}
        >
          {<LocalShippingIcon />}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Delivery Task #{task.id}
              </Typography>
              {showDeliveryTaskState(status)}
            </Box>
            <Typography variant="caption" color="text.secondary">
              <CalendarTodayIcon
                sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }}
              />
              {date}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <Chip
              icon={<AssignmentIcon fontSize="small" />}
              label={`${order.number_of_packages} ${order.number_of_packages === 1 ? 'package' : 'packages'}`}
              size="small"
              variant="outlined"
            />

            {driver && (
              <Chip
                icon={<Moped fontSize="small" />}
                label={`${driver.name} ${driver.phone_number ? `(${driver.phone_number})` : ''}`}
                size="small"
                variant="outlined"
              />
            )}

            {!isMerchant && user_is_admin(user) && (
              <>
                {' '}
                {
                  <DeliveryTaskCommission
                    task={task}
                    updateDeliveryTask={(t) => updateDeliveryTask(t)}
                    disabled={!canEditOrderBasedOnBranch}
                  />
                }
              </>
            )}
          </Box>

          {failureReason && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                backgroundColor: 'error',
                p: 1,
                borderRadius: 1,
                mb: 1,
              }}
            >
              <ErrorIcon color="error" fontSize="small" />
              <Typography variant="body2" color="error.main">
                {failureReason}
              </Typography>
            </Box>
          )}

          <Box display={'flex'} gap={1} flexWrap="wrap" mt={1} mb={1}>
            {!isMerchant &&
              task.driver_invoice &&
              renderInvoiceChip(
                task.driver_invoice,
                'driver',
                lockedInvoice?.id === task.driver_invoice.id &&
                  invoiceLockOperation === 'view'
              )}

            {!isMerchant &&
              task.commission_invoice &&
              renderInvoiceChip(
                task.commission_invoice,
                'commission',
                lockedInvoice?.id === task.commission_invoice.id &&
                  invoiceLockOperation === 'view'
              )}

            {task.status == OrderDeliveryTaskState.COMPLETED &&
              order.merchant_invoice && (
                <Chip
                  sx={{ mb: 1 }}
                  onClick={() =>
                    fetchInvoiceContent(
                      order.merchant_invoice ?? null,
                      'merchant'
                    )
                  }
                  disabled={lockedInvoice != null}
                  icon={
                    lockedInvoice?.id === order.merchant_invoice.id &&
                    invoiceLockOperation === 'view' ? (
                      <CircularProgress size={20} />
                    ) : order.merchant_invoice.status ==
                      MerchantOrdersInvoiceState.PAID ? (
                      <Check />
                    ) : (
                      <Pending fontSize={'small'} />
                    )
                  }
                  color={
                    order.merchant_invoice.status ==
                    MerchantOrdersInvoiceState.PAID
                      ? 'success'
                      : 'warning'
                  }
                  label={`Merchant Invoice #${order.merchant_invoice.id}`}
                />
              )}
          </Box>

          {notes && notes.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                backgroundColor: theme.palette.grey[100],
                p: 1,
                borderRadius: 1,
              }}
            >
              <NotesIcon color="info" fontSize="small" />
              <Box>
                {notes.map((note, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    color="text.secondary"
                  >
                    • {note}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {canForceAction(task) && (
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button
                size="small"
                variant="outlined"
                color="success"
                onClick={() => handleForceAction(task, 'complete')}
                disabled={!canEditOrderBasedOnBranch}
              >
                Force Complete
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => handleForceAction(task, 'fail')}
                disabled={!canEditOrderBasedOnBranch}
              >
                Force Fail
              </Button>
            </Box>
          )}
        </Box>
        {invoiceData !== null && (
          <InvoicePDFDialog
            pdfData={invoiceData}
            onClose={() => {
              setInvoiceData(null);
            }}
          />
        )}
      </Box>
    );
  };

  const renderDeliveryAtWarehouse = () => {
    const date = new Date(order.delivered_at!).toLocaleString();

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <Avatar
          sx={{
            bgcolor: theme.palette.primary.light,
            color: theme.palette.getContrastText(theme.palette.primary.light),
          }}
        >
          {<WarehouseOutlined />}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Delivery At Warehouse
              </Typography>
              {showDeliveryTaskState(OrderDeliveryTaskState.COMPLETED)}
            </Box>
            <Typography variant="caption" color="text.secondary">
              <CalendarTodayIcon
                sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }}
              />
              {date}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <Chip
              icon={<AssignmentIcon fontSize="small" />}
              label={`${order.number_of_packages} ${order.number_of_packages === 1 ? 'package' : 'packages'}`}
              size="small"
              variant="outlined"
            />
          </Box>
          {order.merchant_invoice && (
            <Chip
              sx={{ mb: 1 }}
              onClick={() =>
                fetchInvoiceContent(order.merchant_invoice ?? null, 'merchant')
              }
              disabled={lockedInvoice != null}
              icon={
                lockedInvoice?.id === order.merchant_invoice.id &&
                invoiceLockOperation === 'view' ? (
                  <CircularProgress size={20} />
                ) : order.merchant_invoice.status ==
                  MerchantOrdersInvoiceState.PAID ? (
                  <Check />
                ) : (
                  <Pending fontSize={'small'} />
                )
              }
              color={
                order.merchant_invoice.status == MerchantOrdersInvoiceState.PAID
                  ? 'success'
                  : 'warning'
              }
              label={`Merchant Invoice #${order.merchant_invoice.id}`}
            />
          )}
        </Box>

        {invoiceData !== null && (
          <InvoicePDFDialog
            pdfData={invoiceData}
            onClose={() => {
              setInvoiceData(null);
            }}
          />
        )}
      </Box>
    );
  };
  const ordered_tasks = getSortedTasks(pickupTasks, deliveryTasks);

  return (
    <>
      <TimelineCard
        title="Task History"
        icon={<LocalShippingIcon />}
        color={color}
      >
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {pickupTasks?.length === 0 && deliveryTasks?.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ p: 2 }}
            >
              No task history recorded
            </Typography>
          ) : (
            <List sx={{ py: 0 }}>
              {order.delivery_state == DeliveryState.DELIVERED_AT_WAREHOUSE &&
                renderDeliveryAtWarehouse()}
              {ordered_tasks.map((item, index) => {
                let result: JSX.Element;
                if (item.type == 'pickup')
                  result = renderPickupTaskItem(item.val as IPickupTask);
                else {
                  const delivery_task = item.val as IOrderDeliveryTask;
                  if (delivery_task.status === OrderDeliveryTaskState.SCHEDULED)
                    result = renderDeliveryBatch(
                      delivery_task.batch as IDeliveryBatch
                    );
                  else result = renderDeliveryTask(delivery_task);
                }

                return (
                  <React.Fragment key={index}>
                    {result}
                    {index < ordered_tasks.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>
      </TimelineCard>

      <>
        {openPickupTask != null &&
          (isMerchant ? (
            <MerchantPickupTaskDialog
              taskID={openPickupTask.id}
              onClose={handleClosePickupTask}
            />
          ) : (
            <PickupTaskDialog
              taskID={openPickupTask.id}
              onClose={handleClosePickupTask}
            />
          ))}
      </>
      <>
        {openDeliveryBatch != null && (
          <DeliveryBatchDialog
            taskID={openDeliveryBatch.id}
            onClose={handleCloseDeliveryBatch}
          />
        )}
      </>

      {/* PDF Viewer Dialog for driver invoices */}
      <PDFViewerDialog
        open={showPdfViewer}
        onClose={closePdfViewer}
        pdfUrl={pdfUrl}
        title={pdfTitle}
        filename={pdfFilename}
      />

      {/* Force Delivery Action Dialog */}
      {forceActionTask && (
        <ForceDeliveryActionDialog
          open={forceActionDialogOpen}
          onClose={() => {
            setForceActionDialogOpen(false);
            setForceActionTask(null);
          }}
          task={forceActionTask}
          action={forceActionType}
          order={order}
          onSuccess={handleForceActionSuccess}
        />
      )}
    </>
  );
};
